// Vorgangsarten (#489, Etappe V1) – Tabelle public.vorgangsarten.
// Ein Vorgang ist ein Ticket mit vorgangsart_id; die Vorgangsart bündelt Kategorie, Checkliste,
// Antwortvorlage, Pflichtangaben, Fristen und Freigabestufe. Pflegen dürfen nur Admins
// (RLS is_admin()); die Rollenabfrage hier steuert nur die Oberfläche.
import { supabase } from '../supabase/client';

export type AngabenQuelle = 'app' | 'absender';
export interface Pflichtangabe { schluessel: string; bezeichnung: string; quelle: AngabenQuelle }
export type FreigabeStufe = 'vorschlag' | 'freigabe';

export interface Vorgangsart {
  id: string;
  name: string;
  icon: string | null;
  farbe: string | null;
  sortierung: number;
  aktiv: boolean;
  /** Erklärtext für das Team */
  erklaerung: string | null;
  beschreibung_ki: string | null;
  stichwoerter: string[];
  kategorie_id: string | null;
  unterkategorie_id: string | null;
  checkliste_vorlage_id: string | null;
  antwort_vorlage_id: string | null;
  pflichtangaben: Pflichtangabe[];
  faellig_tage: number | null;
  wiedervorlage_tage: number | null;
  zustaendig_typ: 'mitarbeiter' | 'team' | null;
  zustaendig_id: string | null;
  freigabe_stufe: FreigabeStufe;
  rechtlich_sensibel: boolean;
}

export const FREIGABE_STUFEN: { key: FreigabeStufe; label: string; hinweis: string }[] = [
  { key: 'freigabe', label: 'Mit Freigabe', hinweis: 'Antwort wird als Entwurf vorbereitet und mit einem Klick freigegeben.' },
  { key: 'vorschlag', label: 'Nur Vorschlag', hinweis: 'Die App zeigt den Vorgang nur an; alles wird von Hand angestoßen.' },
];

/** Pflichtangaben aus jsonb robust lesen (unbekannte Einträge fallen weg). */
export function leseAngaben(roh: unknown): Pflichtangabe[] {
  if (!Array.isArray(roh)) return [];
  return roh.flatMap((x): Pflichtangabe[] => {
    if (!x || typeof x !== 'object') return [];
    const o = x as Record<string, unknown>;
    const bezeichnung = typeof o.bezeichnung === 'string' ? o.bezeichnung.trim() : '';
    if (!bezeichnung) return [];
    const schluessel = typeof o.schluessel === 'string' && o.schluessel.trim() ? o.schluessel.trim() : schluesselVon(bezeichnung);
    return [{ schluessel, bezeichnung, quelle: o.quelle === 'app' ? 'app' : 'absender' }];
  });
}

/** „Gewünschter Auszugstermin“ → „gewuenschter_auszugstermin“. */
export function schluesselVon(bezeichnung: string): string {
  return bezeichnung.toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
    .slice(0, 40) || 'angabe';
}

/** Pflichtangaben für die Speicherung: leere weg, Schlüssel eindeutig. */
export function normalisiereAngaben(angaben: readonly Pflichtangabe[]): Pflichtangabe[] {
  const vergeben = new Set<string>();
  return angaben.flatMap(a => {
    const bezeichnung = a.bezeichnung.trim();
    if (!bezeichnung) return [];
    const basis = a.schluessel.trim() || schluesselVon(bezeichnung);
    let s = basis;
    for (let n = 2; vergeben.has(s); n++) s = `${basis}_${n}`;
    vergeben.add(s);
    return [{ schluessel: s, bezeichnung, quelle: a.quelle }];
  });
}

/** Stichwörter aus Freitext (Komma oder Zeile getrennt), klein, ohne Doppelte. */
export function stichwoerterAusText(text: string): string[] {
  const aus: string[] = [];
  for (const t of text.split(/[,\n;]/)) {
    const w = t.trim().toLowerCase();
    if (w && !aus.includes(w)) aus.push(w);
  }
  return aus;
}

/** Tage-Feld: leer → null, sonst ganze Zahl 0–365; ungültig → undefined. */
export function leseTage(text: string): number | null | undefined {
  const t = text.trim();
  if (!t) return null;
  if (!/^\d{1,3}$/.test(t)) return undefined;
  const n = Number(t);
  return n <= 365 ? n : undefined;
}

export interface VorgangsartEntwurf {
  id: string | null;
  name: string;
  aktiv: boolean;
  erklaerung: string;
  beschreibung_ki: string;
  stichwoerter: string;
  kategorie_id: string;
  unterkategorie_id: string;
  checkliste_vorlage_id: string;
  antwort_vorlage_id: string;
  pflichtangaben: Pflichtangabe[];
  faellig_tage: string;
  wiedervorlage_tage: string;
  zustaendig: string; // '' | 'm:<id>' | 't:<id>'
  freigabe_stufe: FreigabeStufe;
  rechtlich_sensibel: boolean;
  sortierung: number;
}

export function leererEntwurf(sortierung: number): VorgangsartEntwurf {
  return {
    id: null, name: '', aktiv: true, erklaerung: '', beschreibung_ki: '', stichwoerter: '', kategorie_id: '', unterkategorie_id: '',
    checkliste_vorlage_id: '', antwort_vorlage_id: '', pflichtangaben: [], faellig_tage: '', wiedervorlage_tage: '',
    zustaendig: '', freigabe_stufe: 'freigabe', rechtlich_sensibel: false, sortierung,
  };
}

export function entwurfAus(v: Vorgangsart): VorgangsartEntwurf {
  return {
    id: v.id, name: v.name, aktiv: v.aktiv, erklaerung: v.erklaerung ?? '', beschreibung_ki: v.beschreibung_ki ?? '', stichwoerter: v.stichwoerter.join(', '),
    kategorie_id: v.kategorie_id ?? '', unterkategorie_id: v.unterkategorie_id ?? '',
    checkliste_vorlage_id: v.checkliste_vorlage_id ?? '', antwort_vorlage_id: v.antwort_vorlage_id ?? '',
    pflichtangaben: v.pflichtangaben, faellig_tage: v.faellig_tage == null ? '' : String(v.faellig_tage),
    wiedervorlage_tage: v.wiedervorlage_tage == null ? '' : String(v.wiedervorlage_tage),
    zustaendig: v.zustaendig_id && v.zustaendig_typ ? `${v.zustaendig_typ === 'team' ? 't' : 'm'}:${v.zustaendig_id}` : '',
    freigabe_stufe: v.freigabe_stufe, rechtlich_sensibel: v.rechtlich_sensibel, sortierung: v.sortierung,
  };
}

export type Zeile = Omit<Vorgangsart, 'id' | 'icon' | 'farbe'>;

/** Prüft den Entwurf; bei Fehlern Liste der Meldungen, sonst die Zeile für die Datenbank. */
export function pruefeEntwurf(e: VorgangsartEntwurf, andereNamen: readonly string[]): { fehler: string[]; zeile: Zeile | null } {
  const fehler: string[] = [];
  const name = e.name.trim();
  if (!name) fehler.push('Bitte einen Namen angeben.');
  else if (andereNamen.some(n => n.trim().toLowerCase() === name.toLowerCase())) fehler.push(`„${name}“ gibt es schon.`);
  const faellig = leseTage(e.faellig_tage);
  const wv = leseTage(e.wiedervorlage_tage);
  if (faellig === undefined) fehler.push('Frist: bitte ganze Tage (0–365) oder leer.');
  if (wv === undefined) fehler.push('Wiedervorlage: bitte ganze Tage (0–365) oder leer.');
  if (e.unterkategorie_id && !e.kategorie_id) fehler.push('Unterkategorie nur zusammen mit einer Kategorie.');
  if (fehler.length) return { fehler, zeile: null };
  const [typ, zid] = e.zustaendig.includes(':') ? e.zustaendig.split(':', 2) as [string, string] : ['', ''];
  return {
    fehler,
    zeile: {
      name, aktiv: e.aktiv, sortierung: e.sortierung,
      erklaerung: e.erklaerung.trim() || null,
      beschreibung_ki: e.beschreibung_ki.trim() || null,
      stichwoerter: stichwoerterAusText(e.stichwoerter),
      kategorie_id: e.kategorie_id || null,
      unterkategorie_id: e.unterkategorie_id || null,
      checkliste_vorlage_id: e.checkliste_vorlage_id || null,
      antwort_vorlage_id: e.antwort_vorlage_id || null,
      pflichtangaben: normalisiereAngaben(e.pflichtangaben),
      faellig_tage: faellig ?? null,
      wiedervorlage_tage: wv ?? null,
      zustaendig_typ: zid ? (typ === 't' ? 'team' : 'mitarbeiter') : null,
      zustaendig_id: zid || null,
      freigabe_stufe: e.freigabe_stufe,
      rechtlich_sensibel: e.rechtlich_sensibel,
    },
  };
}

// ── DB-Wrapper (dünn, Konvention: nicht unit-getestet) ─────────────────────────

const SPALTEN = 'id, name, icon, farbe, sortierung, aktiv, erklaerung, beschreibung_ki, stichwoerter, kategorie_id, unterkategorie_id, checkliste_vorlage_id, antwort_vorlage_id, pflichtangaben, faellig_tage, wiedervorlage_tage, zustaendig_typ, zustaendig_id, freigabe_stufe, rechtlich_sensibel';

export async function ladeVorgangsarten(nurAktive = false): Promise<Vorgangsart[]> {
  let q = supabase.from('vorgangsarten').select(SPALTEN).order('sortierung').order('name');
  if (nurAktive) q = q.eq('aktiv', true);
  const { data, error } = await q;
  if (error) throw error;
  return ((data ?? []) as unknown as (Vorgangsart & { pflichtangaben: unknown; stichwoerter: string[] | null })[])
    .map(v => ({ ...v, stichwoerter: v.stichwoerter ?? [], pflichtangaben: leseAngaben(v.pflichtangaben) }));
}

const ADMIN_ROLLEN = ['admin', 'super admin', 'master admin'];

/** Darf der angemeldete Benutzer Vorgangsarten pflegen? (wie is_admin() in der Datenbank) */
export async function darfVorgaengePflegen(): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;
  const { data } = await supabase.from('profiles').select('role').eq('id', user.id).maybeSingle();
  return ADMIN_ROLLEN.includes(((data?.role as string) || '').toLowerCase());
}
